import { useEffect, useRef, useState } from "react";
import { getVPH } from "@/lib/viewport";

// Generic scroll-driven JPEG-sequence canvas.
//  variant="story": mobile shows scene in top ~68% with a dark title band,
//                   per-scene focal; desktop shows whole frame over a blur.
//  variant="cover": full-bleed cover on all breakpoints over a blurred backdrop.
export const FrameSequenceCanvas = ({
  onProgress,
  sectionSelector,
  total = 128,
  basePath = "/images/janmashtami",
  prefix = "ezgif-frame-",
  pad = 3,
  variant = "story",
  mobileContainUntil = 0,
}) => {
  const canvasRef = useRef(null);
  const imagesRef = useRef([]);
  const frameRef = useRef(0);
  const rafRef = useRef(null);
  const [loaded, setLoaded] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const framePath = (i) =>
    `${basePath}/${prefix}${String(i).padStart(pad, "0")}.jpg`;

  useEffect(() => {
    let mounted = true;
    let count = 0;
    const imgs = new Array(total);
    for (let i = 1; i <= total; i++) {
      const img = new Image();
      img.src = framePath(i);
      img.onload = img.onerror = () => {
        if (!mounted) return;
        count += 1;
        setLoaded(count);
        if (count === total) setIsReady(true);
      };
      imgs[i - 1] = img;
    }
    imagesRef.current = imgs;
    return () => {
      mounted = false;
    };
  }, []);

  const draw = (index) => {
    const canvas = canvasRef.current;
    const img = imagesRef.current[index];
    if (!canvas || !img || !img.width) return;
    const ctx = canvas.getContext("2d");
    const cw = canvas.width;
    const ch = canvas.height;
    const iw = img.width;
    const ih = img.height;
    const isMobile = window.innerWidth < 768;

    ctx.clearRect(0, 0, cw, ch);

    if (variant === "story" && isMobile) {
      const stageH = Math.round(ch * 0.68);
      let fx = 0.5;
      let fy = 0.42;
      const f = index + 1;
      if (f <= 30) {
        fx = 0.66;
        fy = 0.5;
      } else if (f <= 52) {
        fx = 0.5;
        fy = 0.4;
      }
      const scale = Math.max(cw / iw, stageH / ih);
      const dx = cw * 0.5 - iw * scale * fx;
      const dy = stageH * 0.5 - ih * scale * fy;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, cw, stageH);
      ctx.clip();
      ctx.drawImage(img, dx, dy, iw * scale, ih * scale);
      ctx.restore();

      ctx.fillStyle = "#070b18";
      ctx.fillRect(0, stageH, cw, ch - stageH);
      const fade = ch * 0.12;
      const g = ctx.createLinearGradient(0, stageH - fade, 0, stageH + 2);
      g.addColorStop(0, "rgba(7,11,24,0)");
      g.addColorStop(1, "rgba(7,11,24,1)");
      ctx.fillStyle = g;
      ctx.fillRect(0, stageH - fade, cw, fade + 2);
      return;
    }

    // Blurred backdrop
    const coverScale = Math.max(cw / iw, ch / ih) * 1.18;
    const bw = iw * coverScale;
    const bh = ih * coverScale;
    ctx.save();
    ctx.filter = "blur(28px) brightness(0.5) saturate(1.1)";
    ctx.drawImage(img, (cw - bw) / 2, (ch - bh) / 2, bw, bh);
    ctx.restore();

    if (variant === "cover") {
      const sCover = Math.max(cw / iw, ch / ih);
      if (isMobile && mobileContainUntil > 0) {
        // Reclining Krishna: fit edge-to-edge by width, then slowly zoom in to
        // full-screen (cover) as he sits up. Fill the surrounds with the same
        // page-dark colour so it reads as one continuous space (no rectangle).
        const f = index + 1;
        const sFit = cw / iw;
        let scale;
        if (f <= mobileContainUntil) {
          const tt =
            mobileContainUntil > 1 ? (f - 1) / (mobileContainUntil - 1) : 1;
          const eased = tt * tt; // ease-in so the zoom accelerates as he sits
          scale = sFit + (sCover - sFit) * eased;
        } else {
          scale = sCover;
        }
        ctx.fillStyle = "#070b18";
        ctx.fillRect(0, 0, cw, ch);
        const dw = iw * scale;
        const dh = ih * scale;
        ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
        return;
      }
      const dw = iw * sCover;
      const dh = ih * sCover;
      ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
      return;
    }

    // story desktop: whole frame (contain)
    const scale = Math.min(cw / iw, ch / ih) * 0.94;
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
  };

  useEffect(() => {
    let lastW = -1;
    const resize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Only re-layout on width change — ignore height-only (toolbar) resizes.
      if (window.innerWidth === lastW) return;
      lastW = window.innerWidth;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vh = getVPH();
      canvas.width = window.innerWidth * dpr;
      canvas.height = vh * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = vh + "px";
      draw(frameRef.current);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [isReady]);

  useEffect(() => {
    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const section = document.querySelector(sectionSelector);
        if (!section) return;
        const rect = section.getBoundingClientRect();
        const totalScroll = section.offsetHeight - getVPH();
        const scrolled = Math.min(Math.max(-rect.top, 0), totalScroll);
        const progress = totalScroll > 0 ? scrolled / totalScroll : 0;
        const target = Math.min(
          total - 1,
          Math.round(progress * (total - 1))
        );
        if (target !== frameRef.current) {
          frameRef.current = target;
          draw(target);
        }
        onProgress && onProgress(progress);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [isReady, onProgress, sectionSelector]);

  const pct = Math.round((loaded / total) * 100);

  return (
    <>
      <canvas
        ref={canvasRef}
        data-testid="frame-sequence-canvas"
        className="block h-full w-full"
      />
      {!isReady && (
        <div
          data-testid="sequence-loader"
          className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-[#070b18]"
        >
          <p className="font-display text-4xl text-amber-300">श्री कृष्ण</p>
          <div className="mt-6 h-[2px] w-52 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-gradient-to-r from-amber-400 to-amber-200 transition-all duration-200"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="mt-3 text-xs tracking-[0.3em] text-amber-100/40">
            {pct}%
          </p>
        </div>
      )}
    </>
  );
};
